import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { useNotifications } from '../../Context/NotificationContext';
import { getNotificationTypeInfo } from '../../services/notificationService';
import './NotificationBell.scss';


const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  
  const {
    notifications,
    unreadCount,
    loading,
    pagination,
    fetchNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const toggleDropdown = useCallback(() => {
    if (!isOpen) {
      fetchNotifications({ pageNumber: 0 });
    }
    setIsOpen(prev => !prev);
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);


  const handleNotificationClick = useCallback((notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
    if (notification.order_id) {
      navigate(`/order-detail/${notification.order_id}`);
    }
  }, [markAsRead, navigate]);

  const handleMarkAllAsRead = useCallback((e) => {
    e.stopPropagation();
    markAllAsRead();
  }, [markAllAsRead]);

  const handleViewAll = useCallback(() => {
    setIsOpen(false);
    navigate('/notifications');
  }, [navigate]);


  const handleLoadMore = useCallback((e) => {
    e.stopPropagation();
    loadMoreNotifications();
  }, [loadMoreNotifications]);

  const formatBadgeCount = (count) => {
    if (count > 99) return '99+';
    return count.toString();
  };


  const hasMorePages = pagination.pageNumber < pagination.totalPages - 1;

  return (
    <div className="notification-bell-container">
      <div
        ref={bellRef}
        className="notification-bell"
        onClick={toggleDropdown}
        role="button"
        aria-label="Notifications"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && toggleDropdown()}
      >
        <i
          className="pi pi-bell p-overlay-badge"
          style={{ fontSize: '15.5px' }}
        >
          {unreadCount > 0 && (
            <Badge
              value={formatBadgeCount(unreadCount)}
              severity="danger"
            />
          )}
        </i>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div ref={dropdownRef} className="notification-dropdown">
          {/* Dropdown Header */}
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="notification-list">
            {loading && notifications.length === 0 ? (
              <div className="notification-loading">
                <i className="pi pi-spin pi-spinner" />
                <span>Loading notifications...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <i className="pi pi-inbox" />
                <span>No notifications yet</span>
              </div>
            ) : (
              <>
                {notifications.map((notification) => {
                  const typeInfo = getNotificationTypeInfo(notification.type);
                  
                  return (
                    <div
                      key={notification.id}
                      className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                    >
                      {!notification.is_read && (
                        <div className="unread-indicator" />
                      )}
                      <div
                        className="notification-icon"
                        style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}
                      >
                        <i className={`pi ${typeInfo.icon}`} />
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-body">{notification.body}</div>
                        <div className="notification-meta">
                          <span className="notification-time">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {notification.order_id && (
                            <span className="notification-order-link">
                              View Order →
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {hasMorePages && (
                  <div className="notification-load-more">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <i className="pi pi-spin pi-spinner" />
                          Loading...
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="notification-dropdown-footer">
            <button onClick={handleViewAll}>
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
